import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw, Trash2, Star, CircleDot, Github, Loader2, Share2 } from "lucide-react";
import { api } from "../../lib/api";

const SUGGESTIONS = ["facebook/react", "vercel/next.js", "vitejs/vite", "prisma/prisma", "expo/expo"];

export default function Repositories({ repos, onChange, onGoFeed }) {
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [ent, setEnt] = useState(null);

  const loadEnt = () => api.get("/entitlements").then((r) => setEnt(r.data)).catch(() => {});
  useEffect(() => { loadEnt(); }, [repos.length]);

  const atCap = ent && ent.repos_used >= ent.repos_limit;

  const add = async (value) => {
    const target = (value ?? url).trim();
    if (!target) return;
    setAdding(true);
    try {
      const res = await api.post("/repos", { url: target });
      toast.success(`Tracking ${res.data.repo.full_name} · ${res.data.signals_intercepted} signals intercepted`);
      setUrl("");
      await onChange();
      loadEnt();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add repository");
    } finally {
      setAdding(false);
    }
  };

  const sync = async (repo) => {
    setBusyId(repo.id);
    try {
      const res = await api.post(`/repos/${repo.id}/sync`);
      toast.success(`${repo.full_name} re-synced · ${res.data.signals_intercepted} signals`);
      await onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (repo) => {
    setBusyId(repo.id);
    try {
      await api.delete(`/repos/${repo.id}`);
      toast.success(`Stopped tracking ${repo.full_name}`);
      await onChange();
    } catch (e) {
      toast.error("Could not remove repository");
    } finally {
      setBusyId(null);
    }
  };

  const shareRepo = async (repo) => {
    const url = `${window.location.origin}/live?repo=${encodeURIComponent(repo.full_name)}`;
    try {
      if (navigator.share) { await navigator.share({ title: `Friction on ${repo.full_name}`, url }); return; }
    } catch (e) { /* fall through */ }
    try { await navigator.clipboard.writeText(url); toast.success("Public friction-wall link copied"); }
    catch (e) { toast.error("Could not copy link"); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-card border-2 border-foreground shadow-brutal-sm p-5">
        <div className="flex items-start justify-between gap-3 mb-1">
          <h3 className="font-display font-extrabold tracking-tight text-xl">Track a public repository</h3>
          {ent && (
            <span data-testid="repo-usage" className={`font-mono text-[11px] uppercase tracking-widest px-2 py-1 border ${atCap ? "border-primary text-primary" : "border-border text-muted-foreground"}`}>
              {ent.repos_used} / {ent.repos_limit >= 100000 ? "∞" : ent.repos_limit} repos
            </span>
          )}
        </div>
        <p className="font-mono text-xs text-muted-foreground mb-4">Live issues are pulled from GitHub and scored for developer friction.</p>
        {atCap && (
          <div className="font-mono text-[11px] text-primary bg-primary/10 border border-primary px-3 py-2 mb-3" data-testid="repo-cap-note">
            You've hit the {ent.repos_limit}-repository limit on the {ent.plan.charAt(0).toUpperCase() + ent.plan.slice(1)} plan. Upgrade in Settings → Billing to track more.
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex items-center gap-2 flex-1 border-2 border-foreground bg-background px-3">
            <Github size={16} className="text-muted-foreground" />
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="owner/repo"
              onKeyDown={(e) => e.key === "Enter" && !atCap && add()} data-testid="repo-input"
              disabled={atCap}
              className="flex-1 bg-transparent py-3 font-mono text-sm focus:outline-none disabled:opacity-50" />
          </div>
          <button onClick={() => add()} disabled={adding || atCap} data-testid="repo-add-btn"
            className="btn btn-primary">
            {adding ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Intercept
          </button>
        </div>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <span className="font-mono text-[11px] text-muted-foreground">try:</span>
          {SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => add(s)} disabled={adding || atCap} data-testid={`repo-suggest-${s.replace("/", "-")}`}
              className="font-mono text-[11px] border border-border bg-background px-2 py-1 hover:border-foreground hover:bg-muted transition-colors disabled:opacity-40">
              {s}
            </button>
          ))}
        </div>
      </div>

      {repos.length === 0 ? (
        <div className="border-2 border-dashed border-border p-10 text-center font-mono text-sm text-muted-foreground">
          No repositories tracked yet.
        </div>
      ) : (
        <div className="bg-card border border-border overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="px-4 py-3">Repository</th>
                <th className="px-4 py-3">Stars</th>
                <th className="px-4 py-3">Open issues</th>
                <th className="px-4 py-3">Signals</th>
                <th className="px-4 py-3">Lang</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {repos.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors" data-testid={`repo-row-${r.full_name.replace("/", "-")}`}>
                  <td className="px-4 py-3">
                    <button onClick={onGoFeed} className="font-medium hover:text-primary transition-colors">{r.full_name}</button>
                    {r.description && <div className="text-[11px] text-muted-foreground truncate max-w-xs">{r.description}</div>}
                  </td>
                  <td className="px-4 py-3"><span className="flex items-center gap-1"><Star size={12} className="text-accent" /> {(r.stars ?? 0).toLocaleString()}</span></td>
                  <td className="px-4 py-3"><span className="flex items-center gap-1"><CircleDot size={12} className="text-secondary" /> {(r.open_issues ?? 0).toLocaleString()}</span></td>
                  <td className="px-4 py-3 font-bold text-primary">{r.signal_count ?? 0}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.language || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => shareRepo(r)} data-testid={`repo-share-${r.full_name.replace("/", "-")}`}
                        className="p-2 border border-border hover:border-accent hover:text-accent transition-colors" title="Share public friction wall">
                        <Share2 size={14} />
                      </button>
                      <button onClick={() => sync(r)} disabled={busyId === r.id} data-testid={`repo-sync-${r.full_name.replace("/", "-")}`}
                        className="p-2 border border-border hover:border-foreground hover:bg-background transition-colors" title="Re-sync">
                        <RefreshCw size={14} className={busyId === r.id ? "animate-spin" : ""} />
                      </button>
                      <button onClick={() => remove(r)} disabled={busyId === r.id} data-testid={`repo-delete-${r.full_name.replace("/", "-")}`}
                        className="p-2 border border-border hover:border-primary hover:text-primary transition-colors" title="Remove">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
